import React from "react";
import SanitizedHTML from "../../../HTML/SanitizedHTML";

// ============================================================
// FORMAT CURRENCY
// ============================================================

const formatCurrency = (amount, currency) => {
  const value = Number(amount || 0) / 100;
  const currencyCode = (currency || "USD").toUpperCase();

  const formattedValue = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  if (currencyCode === "USD") {
    return `US$ ${formattedValue}`;
  }

  return `${currencyCode} ${formattedValue}`;
};

// ============================================================
// FORMAT RECURRING INTERVAL
// ============================================================

const formatRecurringInterval = (interval, intervalCount = 1) => {
  const count = Number(intervalCount) || 1;

  if (count === 1) {
    return interval;
  }

  return `${count} ${interval}s`;
};

// ============================================================
// FORMAT SUBSCRIPTION INTERVAL
// ============================================================

const formatSubscriptionInterval = (interval, intervalCount = 1) => {
  const count = Number(intervalCount) || 1;

  return `${count}-${interval} subscription`;
};

// ============================================================
// COMPONENT
// ============================================================

const OrderSummary = ({ pricePlan, freeTrialNoCard = false }) => {
  if (!pricePlan) {
    return null;
  }

  const bundles = pricePlan.bundles || [];

  const items = pricePlan.is_bundled ? bundles : [pricePlan];

  return (
    <>
      {/* ================================================== */}
      {/* PRICE PLAN */}
      {/* ================================================== */}

      <p className='checkout-section-title'>
        <span className='checkout-section-title-dot' />
        {pricePlan.name}
      </p>

      <div className='checkout-bundle-total'>
        Usual price:{" "}
        <strong>
          {formatCurrency(pricePlan.selling_price, pricePlan.currency)}
        </strong>
        {pricePlan.is_bundled && bundles.length > 0 && (
          <>
            {" "}
            — {bundles.length} {bundles.length === 1 ? "item" : "items"} bundled
            together
          </>
        )}
      </div>

      {/* ================================================== */}
      {/* DESCRIPTION */}
      {/* ================================================== */}

      {pricePlan.customer_description_html && (
        <SanitizedHTML
          html={pricePlan.customer_description_html}
          className='checkout-offer-description'
        />
      )}

      {/* ================================================== */}
      {/* ITEMS */}
      {/* ================================================== */}

      {items.map((item, index) => (
        <div key={item._id || item.price_id} className='checkout-item'>
          <div className='checkout-item-body'>
            {/* ================================================== */}
            {/* ITEM NAME */}
            {/* ================================================== */}

            <div className='checkout-item-title'>
              {pricePlan.is_bundled
                ? `Bundle item ${index + 1}: ${item.name}`
                : item.name}
            </div>

            {/* ================================================== */}
            {/* PRICE / ACCESS */}
            {/* ================================================== */}

            <div className='checkout-item-meta'>
              {formatCurrency(item.selling_price, item.currency)}
              {" · "}
              {item.recurring
                ? formatSubscriptionInterval(item.interval, item.interval_count)
                : "Lifetime access"}
            </div>

            {/* ================================================== */}
            {/* FEATURES */}
            {/* ================================================== */}

            {item.customer_features_html && (
              <SanitizedHTML
                html={item.customer_features_html}
                className='checkout-item-features'
              />
            )}

            {/* ================================================== */}
            {/* AUTO RENEW */}
            {/* ================================================== */}

            {item.recurring &&
              !freeTrialNoCard &&
              Number(item?.next_price_breakdown?.amount_after_discount) > 0 && (
                <div className='checkout-renew'>
                  ↻ Auto-renews at{" "}
                  {formatCurrency(
                    item.next_price_breakdown.amount_after_discount,
                    item.currency,
                  )}
                  /{formatRecurringInterval(item.interval, item.interval_count)}{" "}
                  until cancelled
                </div>
              )}
          </div>
        </div>
      ))}
    </>
  );
};

export default OrderSummary;
